/**
 * Domain registrars offered when connecting a custom domain. `site` feeds the favicon shown for
 * the provider; `dnsUrl` opens its DNS panel and `tutorialUrl` a guide on adding DNS records.
 * `cnameTutorialUrl` is a guide specific to CNAME records, used for subdomains (which only need a
 * CNAME); providers without one fall back to `tutorialUrl`.
 * "other" has no links: the setup screen then only shows the generic instructions.
 */
export type DomainProvider = {
  key: string;
  name: string;
  site?: string;
  dnsUrl?: string;
  tutorialUrl?: string;
  cnameTutorialUrl?: string;
};

export const DOMAIN_PROVIDERS: DomainProvider[] = [
  {
    key: "registrobr",
    name: "Registro.br",
    site: "registro.br",
    dnsUrl: "https://registro.br/painel/",
    tutorialUrl: "https://registro.br/ajuda/",
  },
  {
    key: "godaddy",
    name: "GoDaddy",
    site: "godaddy.com",
    dnsUrl: "https://dcc.godaddy.com/control/portfolio",
    tutorialUrl: "https://www.godaddy.com/help/add-an-a-record-19238",
    cnameTutorialUrl: "https://www.godaddy.com/help/add-a-cname-record-19236",
  },
  {
    key: "hostinger",
    name: "Hostinger",
    site: "hostinger.com.br",
    dnsUrl: "https://hpanel.hostinger.com/domains",
    tutorialUrl: "https://support.hostinger.com/pt-BR/",
    cnameTutorialUrl: "https://www.hostinger.com/support/4738777-how-to-manage-cname-records-at-hostinger/",
  },
  {
    key: "hostgator",
    name: "HostGator",
    site: "hostgator.com.br",
    dnsUrl: "https://financeiro.hostgator.com.br/",
    tutorialUrl: "https://suporte.hostgator.com.br/",
    cnameTutorialUrl:
      "https://suporte.hostgator.com.br/hc/pt-br/articles/30813120385427-Como-criar-ou-alterar-um-registro-A-MX-TXT-CNAME-e-outros-na-Zona-DNS",
  },
  {
    key: "locaweb",
    name: "Locaweb",
    site: "locaweb.com.br",
    dnsUrl: "https://painel.locaweb.com.br/",
    tutorialUrl: "https://ajuda.locaweb.com.br/",
    cnameTutorialUrl: "https://ajuda.locaweb.com.br/wiki/criar-editar-e-excluir-entradas-hospedagem-de-sites/",
  },
  {
    key: "kinghost",
    name: "KingHost",
    site: "king.host",
    dnsUrl: "https://painel.kinghost.com.br/",
    tutorialUrl: "https://king.host/wiki/",
    cnameTutorialUrl: "https://king.host/wiki/artigo/como-configurar-sua-zona-dns-na-kinghost/",
  },
  {
    key: "cloudflare",
    name: "Cloudflare",
    site: "cloudflare.com",
    dnsUrl: "https://dash.cloudflare.com/",
    tutorialUrl: "https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/",
    cnameTutorialUrl: "https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-subdomain/",
  },
  {
    key: "namecheap",
    name: "Namecheap",
    site: "namecheap.com",
    dnsUrl: "https://ap.www.namecheap.com/domains/list/",
    tutorialUrl:
      "https://www.namecheap.com/support/knowledgebase/article.aspx/319/2237/how-can-i-set-up-an-a-address-record-for-my-domain/",
    cnameTutorialUrl:
      "https://www.namecheap.com/support/knowledgebase/article.aspx/9646/2237/how-to-create-a-cname-record-for-your-domain/",
  },
  {
    key: "squarespace",
    name: "Squarespace",
    site: "squarespace.com",
    dnsUrl: "https://account.squarespace.com/domains",
    tutorialUrl: "https://support.squarespace.com/hc/pt-br",
  },
  { key: "other", name: "Outro" },
];

export const findDomainProvider = (key?: string) => DOMAIN_PROVIDERS.find(provider => provider.key === key);

export const providerFaviconUrl = (provider?: DomainProvider) =>
  provider?.site ? `https://www.google.com/s2/favicons?domain=${provider.site}&sz=128` : undefined;
